import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import CreateOutlinedIcon from "@mui/icons-material/CreateOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { IconButton, Skeleton, Stack, Typography } from "@mui/material";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import EmptyCardDataOperationsData from "assets/images/EmptyCardDataOperationsData.png";
import { EmptyCard } from "components/Empty-card/EmptyCard";
import { DataFilters } from "components/Measure-data/Data-filters/DataFilters";
import { NavigateLink } from "components/Navigate-link/NavigateLink";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import { EMPTY_CARD_CONTENT } from "constants/empty-card";
import { useFormatDateHook, useFormatNumericHook, useModals } from "hooks";
import { useDealInvestorsHook } from "hooks/deal-hooks/use-deal-invetors-hook";
import { throttle } from "lodash";
import { MaterialReactTable, MRT_ColumnDef, MRT_SortingState, useMaterialReactTable } from "material-react-table";
import {
  IApiServiceError,
  useDateFormatSelect,
  useDealActions,
  useMeasureParametersSelector,
  useMonitoringDataFilters
} from "store";
import { useDealDefaultCurrencySelect } from "store/selectors/deal-selectors/deal-selectors";
import {
  CurrencyType,
  MeasureDataDraft,
  MeasureDataTableFilters,
  MeasureDataTableOrderColumn,
  MeasureDataTableRow,
  MonitoringDataFiles,
  Order,
  WideMeasureItem,
  WideRow
} from "store/types";

import { Drawer } from "../../Drawer/Drawer";
import { GroupsForm } from "../../Groups-form/GroupsForm";
import {
  getDefaultGroup,
  getDefaultMeasureData,
  Group,
  GroupFormDealData,
  MeasureData,
  MeasureDataGroup,
  MeasureDataRound
} from "../../Groups-form/GroupsFormContext";
import { buildWideData } from "./helpers";
import { SetColumnsButton } from "./SetColumnsButton";

const RESERVED_HEIGHT = 150;

// ─── Measure cell with hover pencil ──────────────────────────────────────────

interface MeasureCellProps {
  row: WideRow;
  measure: string;
  onEdit: (row: WideRow, measure: string) => void;
  editable: boolean;
}

const MeasureCell: React.FC<MeasureCellProps> = ({ row, measure, onEdit, editable }) => {
  const cell = row.cells[measure];
  return (
    <Stack direction="row" alignItems="center" gap={0.5}>
      <span>{cell?.displayValue ?? "—"}</span>
      {cell && editable && (
        <IconButton
          sx={{
            p: 0.25,
            opacity: 0,
            pointerEvents: "none",
            transition: "opacity 0.15s",
            "tr:hover &": { opacity: 1, pointerEvents: "auto" }
          }}
          onClick={() => onEdit(row, measure)}
        >
          <CreateOutlinedIcon sx={{ fontSize: 14 }} />
        </IconButton>
      )}
    </Stack>
  );
};

// ─── Local edit state ─────────────────────────────────────────────────────────

interface WideEditState {
  initialGroups: Group[];
  initialMeasureData: MeasureData[];
  initialRoundsByDeal: Record<number, MeasureDataRound[]>;
  dealData: GroupFormDealData;
  dealTitle: string;
}

// ─── Main Component ───────────────────────────────────────────────────────────

interface DealWideProps {
  dealId?: number;
  setIsEmpty?: (value: boolean) => void;
  editable?: boolean;
}

export const DealWide: React.FC<DealWideProps> = ({ dealId, setIsEmpty, editable = true }) => {
  const {
    getMeasureDataTableAction,
    deleteMeasureDataAction,
    getDealAction,
    createManyMeasureDataAction,
    saveMeasureDataDraftAction,
    setMeasureDataTableFiltersAction
  } = useDealActions();
  const { deleteModal, errorModal } = useModals();
  const queryClient = useQueryClient();
  const currency = (useDealDefaultCurrencySelect() || "USD") as CurrencyType;
  const { dealInvestors, refetchInvestors } = useDealInvestorsHook(dealId);
  const measureParameters = useMeasureParametersSelector();
  const formatDate = useFormatDateHook();
  const formatNum = useFormatNumericHook();
  const dateFormat = useDateFormatSelect();

  const filters = useMonitoringDataFilters();
  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>({});
  const [editState, setEditState] = useState<WideEditState | null>(null);
  const [sorting, setSorting] = useState<MRT_SortingState>([{ id: "date", desc: true }]);

  // ── Paginated fetch ───────────────────────────────────────────────────────
  const [allRows, setAllRows] = useState<MeasureDataTableRow[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  const updateFilters = useCallback(
    (data: Partial<MeasureDataTableFilters> | null) => {
      setAllRows([]);
      setHasMore(true);
      setPage(1);
      setMeasureDataTableFiltersAction(data === null ? null : { ...filters, ...data });
    },
    [filters, setMeasureDataTableFiltersAction]
  );

  const handleSortingChange = useCallback(
    (updater: MRT_SortingState | ((prev: MRT_SortingState) => MRT_SortingState)) => {
      setAllRows([]);
      setHasMore(true);
      setPage(1);
      setSorting((prev) => (typeof updater === "function" ? updater(prev) : updater));
    },
    []
  );

  const apiSort = useMemo(() => {
    if (!sorting.length) return { order: "desc" as Order, orderBy: "date" as MeasureDataTableOrderColumn };
    return { order: (sorting[0].desc ? "desc" : "asc") as Order, orderBy: sorting[0].id as MeasureDataTableOrderColumn };
  }, [sorting]);

  // dealId is optional: when absent (global view) all deals are fetched
  const {
    data: tableData,
    isLoading,
    isFetching
  } = useQuery(
    ["deal-wide-data", dealId, page, filters, sorting],
    () =>
      getMeasureDataTableAction({
        page,
        per_page: 100,
        showTopInvestors: true,
        is_general: !dealId,
        filters: {
          ...filters,
          ...(dealId ? { deals: [{ id: dealId, title: "" }] } : {})
        },
        sort: apiSort
      }),
    {
      enabled: true,
      keepPreviousData: true,
      onSuccess: (d) => {
        setAllRows((prev) => (page === 1 ? d.rows : [...prev, ...d.rows]));
        setHasMore(Number(d.current_page) < d.last_page);
        if (page === 1) setIsEmpty?.(d.rows.length === 0);
      }
    }
  );

  const refetchAll = useCallback(() => {
    setAllRows([]);
    setHasMore(true);
    setPage(1);
    queryClient.invalidateQueries(["deal-wide-data", dealId]);
    refetchInvestors();
  }, [dealId, queryClient, refetchInvestors]);

  // ── Infinite scroll via MRT onScroll ──────────────────────────────────────
  // Use refs to avoid stale closures in the scroll handler
  const scrollStateRef = useRef({ isFetching, hasMore });
  useEffect(() => {
    scrollStateRef.current = { isFetching, hasMore };
  });

  const throttledCheck = useMemo(
    () =>
      throttle((scrollHeight: number, clientHeight: number, scrollTop: number) => {
        const { isFetching: fetching, hasMore: more } = scrollStateRef.current;
        if (fetching || !more) return;
        if (scrollHeight - clientHeight - scrollTop < RESERVED_HEIGHT) {
          setPage((prev) => prev + 1);
        }
      }, 200),
    []
  );

  const handleTableScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const { scrollHeight, clientHeight, scrollTop } = e.currentTarget;
      throttledCheck(scrollHeight, clientHeight, scrollTop);
    },
    [throttledCheck]
  );

  // ── Build wide rows ─────────────────────────────────────────────────────────
  const { wideRows, measureColumns } = useMemo(() => {
    if (!allRows.length) return { wideRows: [], measureColumns: [] };
    return buildWideData(allRows, formatNum);
  }, [allRows, formatNum]);

  // ── Edit handlers ───────────────────────────────────────────────────────────
  const openEdit = useCallback(
    async (row: WideRow, measure?: string) => {
      if (!dealId) return;
      try {
        const items: WideMeasureItem[] = measure
          ? row.measureItems.filter((i) => i.measure === measure)
          : row.measureItems;

        if (!items.length) return;

        const dealInfo = await getDealAction(dealId);

        // investor_id and is_negative are not returned by the table endpoint — resolve both
        const investorNameToId = new Map(dealInvestors.map((inv) => [inv.name, inv.id]));
        const measureIdToIsNegative = new Map(measureParameters.map((p) => [p.id, p.is_negative]));

        // Group items by measure — one Group per unique measure_id / measure name
        const groupsByMeasure = new Map<number | string, { groupId: number; items: WideMeasureItem[] }>();
        let groupCounter = 1;
        items.forEach((item) => {
          const key = item.measure_id ?? item.measure;
          if (!groupsByMeasure.has(key)) groupsByMeasure.set(key, { groupId: groupCounter++, items: [] });
          groupsByMeasure.get(key)!.items.push(item);
        });

        const initialGroups: Group[] = [];
        const initialMeasureData: MeasureData[] = [];
        let measureKey = 1;

        groupsByMeasure.forEach(({ groupId, items: groupItems }) => {
          const first = groupItems[0];
          const rowDealId = first.deal_id || dealId;
          initialGroups.push(
            getDefaultGroup({
              id: groupId,
              deal_id: rowDealId,
              measure_id: first.measure_id,
              original_measure_id: first.measure_id,
              original_group_measure_data_ids: groupItems.map((i) => i.id),
              total_amount: groupItems.reduce((acc, i) => acc + Number(i.value ?? 0), 0),
              delete_measure_data_ids: [],
              ...(dealId !== rowDealId && { sub_deal_id: rowDealId })
            })
          );

          groupItems.forEach((item) => {
            const isNegative = item.measure_id ? (measureIdToIsNegative.get(item.measure_id) ?? false) : false;
            const value = isNegative ? (Number(item.value) * -1).toString() : item.value;
            initialMeasureData.push(
              getDefaultMeasureData({
                key: measureKey++,
                group_id: groupId,
                id: item.id,
                value,
                investor_id:
                  item.investor_id ?? (item.investor_name ? (investorNameToId.get(item.investor_name) ?? null) : null),
                investor_name: item.investor_name,
                round_id: item.round_id,
                date: item.date,
                accounting_date: item.accounting_date,
                effective_range_from: item.effective_range_from,
                effective_range_to: item.effective_range_to,
                currency: item.currency,
                description: item.description,
                split_id: item.split_id ?? undefined,
                batch_id: item.batch_id ?? undefined,
                ...(item.files?.length && {
                  files: {
                    localFiles: null,
                    dbFiles: item.files.reduce(
                      (acc, f) => ({ ...acc, [crypto.randomUUID()]: f }),
                      {} as Record<string, MonitoringDataFiles>
                    )
                  }
                })
              })
            );
          });
        });

        // Build rounds from item data
        const roundsMap = new Map<number, MeasureDataRound>();
        items.forEach((item) => {
          if (!item.round_id) return;
          if (!roundsMap.has(item.round_id)) {
            roundsMap.set(item.round_id, {
              id: item.round_id,
              name: item.round_name ?? "",
              is_deleted: false,
              investors: []
            });
          }
          const resolvedInvestorId =
            item.investor_id ?? (item.investor_name ? (investorNameToId.get(item.investor_name) ?? null) : null);
          if (resolvedInvestorId) {
            const round = roundsMap.get(item.round_id)!;
            if (!round.investors.some((inv) => inv.id === resolvedInvestorId)) {
              round.investors.push({
                id: resolvedInvestorId,
                name: item.investor_name ?? "",
                enable_split: false,
                investor_group: null
              });
            }
          }
        });

        const currencies = dealInfo.currency?.data || [];
        const dealData: GroupFormDealData = {
          deal_id: dealId,
          defaultCurrency:
            currencies.length === 1
              ? (currencies[0].value as CurrencyType)
              : ((currencies.find((c: { is_default: boolean; value: string }) => c.is_default)?.value ??
                  currency) as CurrencyType),
          subDeals: dealInfo.sub_assets.map((s: { id: number; title: string }) => ({ id: s.id, title: s.title })),
          parentId: dealInfo.parent?.id || null
        };

        setEditState({
          initialGroups,
          initialMeasureData,
          initialRoundsByDeal: roundsMap.size ? { [dealId]: Array.from(roundsMap.values()) } : {},
          dealData,
          dealTitle: dealInfo.title
        });
      } catch (err) {
        errorModal({ msg: (err as IApiServiceError)?.message });
      }
    },
    [dealId, getDealAction, currency, dealInvestors, measureParameters, errorModal]
  );

  const handleEditRow = useCallback((row: WideRow) => openEdit(row), [openEdit]);
  const handleEditCell = useCallback((row: WideRow, measure: string) => openEdit(row, measure), [openEdit]);

  const handleEditClose = useCallback(() => {
    setEditState(null);
  }, []);

  const handleSave = useCallback(
    async (data: { groups: MeasureDataGroup[]; roundsByDeals: Record<number, MeasureDataRound[]> }) => {
      await createManyMeasureDataAction({ ...data, edit_type: "split" });
      setEditState(null);
      refetchAll();
    },
    [createManyMeasureDataAction, refetchAll]
  );

  const handleSaveAsDraft = useCallback(
    async (data: MeasureDataDraft["data"]) => {
      if (!dealId) return;
      await saveMeasureDataDraftAction({ deal_id: dealId, data });
      setEditState(null);
      refetchAll();
    },
    [dealId, saveMeasureDataDraftAction, refetchAll]
  );

  // ── Delete handler ──────────────────────────────────────────────────────────
  const handleDeleteRow = useCallback(
    (row: WideRow) => {
      const ids = row.allDataIds;
      if (!ids.length) return;
      const subject = row.isAggregated ? `batch entry for ${row.date}` : `${row.investor ?? "entry"} on ${row.date}`;
      deleteModal({
        element: "entry",
        message: `This will delete ${ids.length} measure entr${ids.length === 1 ? "y" : "ies"} for ${subject}. This action cannot be undone.`,
        action: async () => {
          await Promise.all(ids.map((id) => deleteMeasureDataAction(id)));
          refetchAll();
        }
      });
    },
    [deleteModal, deleteMeasureDataAction, dealId, queryClient]
  );

  // ── Stable ref for callbacks used inside columns ────────────────────────────
  // useFormatDateHook() returns a new function reference every render, which
  // would cause useMemo(columns) to recompute → MRT internal state reset →
  // infinite re-render loop. Capture everything in a ref instead.
  const cbRef = useRef({ handleEditRow, handleEditCell, handleDeleteRow, formatDate, dateFormat, editable });
  useEffect(() => {
    cbRef.current = { handleEditRow, handleEditCell, handleDeleteRow, formatDate, dateFormat, editable };
  });

  // ── MRT column definitions ──────────────────────────────────────────────────
  const columns = useMemo<MRT_ColumnDef<WideRow>[]>(() => {
    const dealCol: MRT_ColumnDef<WideRow>[] = !dealId
      ? [
          {
            id: "deal",
            header: "Deal",
            accessorFn: (row) => row.deal ?? "—",
            size: 180,
            enableColumnOrdering: false,
            enableSorting: true,
            Cell: ({ row }) =>
              row.depth > 0 ? null : row.original.dealId ? (
                <NavigateLink
                  route="dealDashboard"
                  params={{ deal_id: String(row.original.dealId) }}
                  allowNewTab
                  style={{ color: "#26a6ae" }}
                >
                  {row.original.deal ?? "—"}
                </NavigateLink>
              ) : (
                <span>{row.original.deal ?? "—"}</span>
              )
          }
        ]
      : [];

    const fixed: MRT_ColumnDef<WideRow>[] = [
      {
        id: "date",
        header: "Date",
        accessorFn: (row) =>
          row.date ? cbRef.current.formatDate({ date: row.date, format: cbRef.current.dateFormat }) : "—",
        Cell: ({ row }) =>
          row.depth > 0 ? null : (
            <span>
              {row.original.date
                ? cbRef.current.formatDate({ date: row.original.date, format: cbRef.current.dateFormat })
                : "—"}
            </span>
          ),
        enableColumnOrdering: false,
        enableSorting: true,
        size: 110
      },
      {
        id: "round",
        accessorKey: "round",
        header: "Round",
        enableSorting: true,
        size: 120
      },
      {
        id: "investor",
        header: "Investor",
        enableSorting: false,
        accessorFn: (row) => (row.isAggregated ? "All investors" : (row.investor ?? "—")),
        Cell: ({ row }) =>
          row.original.isAggregated ? (
            <Typography component="span" sx={{ fontSize: "inherit", fontStyle: "italic", color: "#71A0B2" }}>
              All investors
            </Typography>
          ) : (
            <span>{row.original.investor ?? "—"}</span>
          ),
        size: 160
      },
      {
        id: "bankAccount",
        header: "Bank Account",
        accessorFn: (row) => row.bankAccount ?? "—",
        enableSorting: false,
        size: 140
      }
    ];

    const measureCols: MRT_ColumnDef<WideRow>[] = measureColumns.map((measure) => ({
      id: measure,
      header: measure,
      accessorFn: (row) => row.cells[measure]?.displayValue ?? "—",
      enableSorting: false,
      Cell: ({ row }) => (
        <MeasureCell
          row={row.original}
          measure={measure}
          onEdit={(r, m) => cbRef.current.handleEditCell(r, m)}
          editable={cbRef.current.editable}
        />
      ),
      size: 130
    }));

    if (!editable) return [...dealCol, ...fixed, ...measureCols];

    const actionCol: MRT_ColumnDef<WideRow> = {
      id: "_actions",
      header: "",
      enableColumnFilter: false,
      enableColumnOrdering: false,
      enableSorting: false,
      enableResizing: false,
      enablePinning: true,
      size: 72,
      minSize: 72,
      maxSize: 72,
      muiTableHeadCellProps: {
        sx: {
          padding: "0 !important",
          backgroundColor: "#edf1f9 !important",
          borderBottom: "1px solid #DBE8F5 !important",
          "&::before": { display: "none" }
        }
      },
      muiTableBodyCellProps: {
        className: "pinned-actions-cell",
        sx: {
          padding: "0 !important",
          position: "sticky",
          right: 0,
          zIndex: 1,
          borderBottom: "1px solid #DBE8F5 !important",
          "&::before": { display: "none" }
        }
      },
      Cell: ({ row }) => (
        <Stack
          direction="row"
          justifyContent="flex-end"
          alignItems="center"
          className="row-actions"
          onClick={(e) => e.stopPropagation()}
          sx={{
            height: "100%",
            px: "4px",
            opacity: 0,
            pointerEvents: "none",
            transition: "opacity 0.15s"
          }}
        >
          <IconButton onClick={() => cbRef.current.handleEditRow(row.original)} sx={{ padding: "4px" }}>
            <CreateOutlinedIcon sx={{ fontSize: 16 }} />
          </IconButton>
          <IconButton onClick={() => cbRef.current.handleDeleteRow(row.original)} sx={{ padding: "4px" }}>
            <DeleteOutlineIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Stack>
      )
    };

    return [...dealCol, ...fixed, ...measureCols, actionCol];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [measureColumns, dealId]);

  // ── MRT table instance ──────────────────────────────────────────────────────
  const table = useMaterialReactTable<WideRow>({
    columns,
    data: wideRows,
    getRowId: (row) => row.key,
    defaultColumn: {
      Header: ({ column }) => {
        const label = column.columnDef.header as string;
        if (!label) return null;
        return (
          <MaterialTooltip title={label} isShow placement="top" variant="dark">
            <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "block" }}>
              {label}
            </span>
          </MaterialTooltip>
        );
      }
    },
    getSubRows: (row) => (row.children.length > 0 ? row.children : undefined),

    // Enabled features
    enableColumnFilters: false,
    enableColumnActions: false,
    enableColumnOrdering: true,
    enableExpanding: true,
    enableStickyHeader: true,
    enableColumnResizing: true,
    columnResizeMode: "onChange",

    // Sorting
    enableSorting: true,
    manualSorting: true,
    enablePagination: false,
    enableRowSelection: false,
    enableGlobalFilter: false,
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    enableTopToolbar: false,
    enableBottomToolbar: false,

    state: { columnVisibility, columnPinning: { right: ["_actions"] }, sorting },
    onSortingChange: handleSortingChange,
    enableColumnPinning: true,
    onColumnVisibilityChange: setColumnVisibility,
    enableHiding: true,

    layoutMode: "grid",

    icons: {
      DragHandleIcon: () => <DragIndicatorIcon sx={{ fontSize: 16 }} />
    },

    muiTablePaperProps: {
      sx: {
        boxShadow: "none",
        border: "1px solid #DBE8F5",
        backgroundColor: "#FFF",
        borderRadius: "8px",
        display: "flex",
        flexDirection: "column",
        flexGrow: 1,
        minHeight: 0,
        overflow: "hidden",
        "& th[data-pinned='true']::after, & td[data-pinned='true']::after": { boxShadow: "none !important" },
        "& th[data-pinned='true'], & td[data-pinned='true']": { boxShadow: "none !important" }
      }
    },
    muiTableContainerProps: {
      onScroll: handleTableScroll,
      sx: { flexGrow: 1, maxHeight: "100%" }
    },
    muiTableHeadCellProps: () => ({
      sx: {
        bgcolor: "#edf1f9 !important",
        fontSize: "13px",
        fontWeight: 500,
        color: "#071F35",
        borderBottom: "1px solid #DBE8F5",
        py: "4px",
        px: "16px",
        whiteSpace: "nowrap",
        overflow: "hidden",
        "& .Mui-TableHeadCell-Content": { height: "100%" },
        "& .Mui-TableHeadCell-Content-Labels": { flex: 1, minWidth: 0, overflow: "hidden" },
        "& .Mui-TableHeadCell-Content-Wrapper": {
          width: "100%",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis"
        },
        "& .MuiDivider-root:not(.Mui-TableHeadCell-ResizeHandle-Divider)": { display: "none" },
        "& .Mui-TableHeadCell-ResizeHandle-Divider": {
          borderColor: "#bcbfcf !important",
          opacity: 0,
          borderWidth: "1px !important",
          transition: "opacity 0.15s"
        },
        "&:hover .Mui-TableHeadCell-ResizeHandle-Divider": {
          opacity: 1
        },
        "&::before": { display: "none" }
      }
    }),
    muiTableHeadRowProps: {
      sx: {
        boxShadow: "none",
        height: "40px",
        minHeight: "40px",
        "& th": { height: "40px", minHeight: "40px" }
      }
    },
    muiTableBodyCellProps: ({ row, column }) => ({
      sx: {
        fontSize: "13px",
        color: "#071F35",
        borderBottom: "1px solid #dbe8f5",
        height: 40,
        py: "4px",
        px: "16px",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        "tr:hover &": { bgcolor: "#f7f8fa !important" }
        // ...(row.depth > 0 && column.id === "date" && { paddingLeft: `${16 + row.depth * 24}px` })
      }
    }),
    muiTableBodyRowProps: ({ row }) => ({
      sx: {
        height: "40px",
        minHeight: "40px",
        bgcolor: row.depth > 0 ? "rgba(246,248,252,0.5)" : "white",
        "&:hover .row-actions": { opacity: 1, pointerEvents: "auto" as const },
        "&:hover td": { bgcolor: "#f7f8fa !important" },
        "&:hover .pinned-actions-cell": { bgcolor: "#f7f8fa !important" },
        "&:last-child td": { borderBottom: "none" }
      }
    })
  });

  // ── Render ──────────────────────────────────────────────────────────────────

  const isEmpty = tableData && allRows.length === 0 && !isFetching;
  const isLoadingInitial = !allRows.length && !isEmpty;

  return (
    <Stack sx={{ flexGrow: 1, minHeight: 0, overflow: "hidden" }}>
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" sx={{ gap: "10px", flexShrink: 0 }}>
        <DataFilters filters={filters} setFilters={updateFilters} disabled={isLoading} hideShowDeleted={true} />
        <SetColumnsButton
          measureColumns={measureColumns}
          columnVisibility={columnVisibility}
          onToggle={(col) => setColumnVisibility((prev) => ({ ...prev, [col]: prev[col] === false }))}
          onToggleAll={(show) =>
            setColumnVisibility(
              measureColumns.reduce<Record<string, boolean>>((acc, col) => {
                acc[col] = show;
                return acc;
              }, {})
            )
          }
        />
      </Stack>

      {isLoadingInitial && (
        <Stack gap={1} sx={{ p: 2 }}>
          {Array.from({ length: 10 }).map((_, i) => (
            <Skeleton key={i} variant="rectangular" height={40} sx={{ borderRadius: 1 }} />
          ))}
        </Stack>
      )}
      {isEmpty && (
        <EmptyCard
          title={EMPTY_CARD_CONTENT.measureDataTable.title}
          description={{ text: EMPTY_CARD_CONTENT.measureDataTable.description }}
          image={{ src: EmptyCardDataOperationsData, sx: { width: 315, height: 140 } }}
          fitPage
        />
      )}
      {!isLoadingInitial && !isEmpty && <MaterialReactTable table={table} />}
      {isFetching && !isLoadingInitial && (
        <Skeleton sx={{ bgcolor: "#b2bec3", height: "60px", width: "100%", flexShrink: 0 }} variant="rectangular" />
      )}

      {/* Edit Drawer */}
      {editState && (
        <Drawer open handleClose={handleEditClose} title={`Edit value - ${editState.dealTitle}`}>
          <GroupsForm
            {...editState.dealData}
            initialGroups={editState.initialGroups}
            initialMeasureData={editState.initialMeasureData}
            initialRoundsByDeal={editState.initialRoundsByDeal}
            onSave={handleSave}
            onSaveAsDraft={handleSaveAsDraft}
            onClose={handleEditClose}
            disableAddGroup
            disableRemoveGroup
            disableDealSelect
            disableSubDealSelect
          />
        </Drawer>
      )}
    </Stack>
  );
};
